import { useTranslation } from 'react-i18next';

import { Menu } from '@headlessui/react';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { Cog8ToothIcon, NewspaperIcon, SparklesIcon, UserGroupIcon, UserPlusIcon } from '@heroicons/react/24/solid';
import { Form, useRouteLoaderData } from '@remix-run/react';

import Button from '~/components/Button';
import Dropdown from '~/components/Dropdown';
import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import useIsMobile from '~/hooks/useIsMobile';
import type { Publication, WebThemeV2 } from '~/interfaces/publication';

import DropdownLink from './DropdownLink';
import EnablePushNotification from './EnablePushNotification';
import InstallApp from './InstallApp';
import NavGroups from './NavGroups';

interface Props {
  theme: WebThemeV2;
  publication: Publication;
  dropdownDirection?: 'left' | 'right' | 'center';
}

const MobileDropdown = ({ theme, dropdownDirection = 'right', publication }: Props) => {
  const { subscription } = useRouteLoaderData<any>('root');
  const { t } = useTranslation();
  const { handleOpenModal } = useGlobalModals();
  const isMobile = useIsMobile();
  const isLoggedIn = Boolean(subscription);

  const { web_template, premium_enabled, has_referral_program } = publication;

  const hasAuthorsPage = web_template?.author_ids?.length >= 1;
  const hasMultiplePublications = web_template?.multi_pub_ids?.length >= 1;
  const isPremiumTierSubscriber = subscription?.tier === 'premium';
  const isPremiumEnabled = premium_enabled;
  const isReferralEnabled = has_referral_program;
  const subscriberId = subscription?.id;
  const avatarUrl = subscription?.avatar_url;

  const handleLogin = () => {
    handleOpenModal('login');
  };

  const handleSubscribe = () => {
    handleOpenModal('signup');
  };

  return (
    <Dropdown
      direction={dropdownDirection}
      className="z-50 sm:w-[300px]"
      style={{
        background: theme?.navbar_background_color || '#fff',
        width: isMobile ? 'calc(100vw - 32px)' : undefined,
      }}
      buttonChild={
        <div
          className="mt-1.5 rounded hover:bg-black/5"
          style={{
            color: theme?.navbar_text_color || '#000',
          }}
        >
          {avatarUrl ? (
            <div className="h-6 w-6 overflow-hidden rounded-full">
              <img className="h-6 w-6 rounded-full" src={avatarUrl} alt="The logged in user" />
            </div>
          ) : (
            <Bars3Icon className="h-6 w-6" />
          )}
        </div>
      }
    >
      <NavGroups publication={publication} />
      {/** FIXED MENU ITEMS */}
      <div>
        {isPremiumEnabled && !isPremiumTierSubscriber && (
          // biome-ignore lint/complexity/noUselessFragments: Keeping for backwards compatibility
          <>
            {publication.web_template.external_upgrade_page_href ? (
              <DropdownLink
                text="Upgrade"
                link={{
                  href: publication.web_template.external_upgrade_page_href,
                  type: 'external',
                }}
                Icon={SparklesIcon}
              />
            ) : (
              <DropdownLink
                text="Upgrade"
                link={{
                  href: '/upgrade',
                  type: 'internal',
                }}
                Icon={SparklesIcon}
              />
            )}
          </>
        )}
        {hasMultiplePublications && (
          <DropdownLink
            text="Newsletters"
            link={{
              href: '/publications',
              type: 'internal',
            }}
            Icon={NewspaperIcon}
          />
        )}
        {hasAuthorsPage && (
          <DropdownLink
            text={t('author.authors')}
            Icon={UserGroupIcon}
            link={{
              href: '/authors',
              type: 'internal',
            }}
          />
        )}
        {isLoggedIn && (
          <DropdownLink
            text={t('nav.links.manage')}
            link={{
              href: `/subscribe/${subscriberId}/manage`,
              type: 'internal',
            }}
            Icon={Cog8ToothIcon}
          />
        )}
        {isReferralEnabled && subscriberId && (
          <DropdownLink
            text={t('nav.links.referrals')}
            link={{
              href: `/subscribe/${subscriberId}/referrals`,
              type: 'internal',
            }}
            Icon={UserPlusIcon}
          />
        )}
        {web_template?.pwa_enabled && <InstallApp />}
        {web_template?.pwa_enabled && <EnablePushNotification />}
      </div>
      {isLoggedIn ? (
        <div className="flex w-full flex-col p-2">
          <Form method="post" action="/logout">
            <Button
              variant="ghost"
              type="submit"
              style={{
                background: theme.navbar_button_secondary_background_color,
                borderColor: theme.navbar_button_secondary_border_color,
                color: theme.navbar_button_secondary_text_color,
                fontFamily: theme.font_button,
                fontWeight: 500,
                width: '100%',
              }}
            >
              {t('shared.cta.logout')}
            </Button>
          </Form>
        </div>
      ) : (
        <div className="flex flex-col space-y-2 p-2">
          <Menu.Item>
            <Button
              variant="ghost"
              onClick={handleLogin}
              style={{
                background: theme.navbar_button_secondary_background_color,
                borderColor: theme.navbar_button_secondary_border_color,
                color: theme.navbar_button_secondary_text_color,
                fontFamily: theme.font_button,
                fontWeight: 500,
              }}
            >
              {t('shared.cta.login')}
            </Button>
          </Menu.Item>
          <Menu.Item>
            <Button
              variant="primary"
              onClick={handleSubscribe}
              style={{
                background: theme.navbar_button_primary_background_color,
                border: `1px solid ${theme.navbar_button_primary_background_color}`,
                color: theme.navbar_button_primary_text_color,
                fontFamily: theme.font_button,
                fontWeight: 500,
              }}
            >
              {publication.cta_text}
            </Button>
          </Menu.Item>
        </div>
      )}
    </Dropdown>
  );
};

export default MobileDropdown;
